'use client';

export default function DashboardSection() {
  return (
    <section
      id="dashboard"
      className="py-24 md:py-32 px-5 md:px-8 bg-surface"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-14">
          <span className="badge badge-coral mb-4">Your Dashboard</span>
          <h2 className="display-md text-foreground mb-5">
            A Real Platform.
            <br />
            <span className="gradient-text">Not a Spreadsheet.</span>
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            Every coach gets their own dashboard at{' '}
            <a
              href="https://app.elevatexgroup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-primary hover:underline"
            >
              app.elevatexgroup.com
            </a>
            . Manage clients, view reports, track check-ins, and monitor RAG statuses — all in one place.
          </p>
        </div>

        {/* Dashboard screenshot placeholder */}
        <div className="relative rounded-3xl overflow-hidden border border-black/[0.07] shadow-2xl bg-white mb-8">
          {/* Browser chrome mockup */}
          <div className="bg-gray-100 border-b border-black/[0.07] px-5 py-3 flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 bg-white rounded-md px-4 py-1.5 text-xs text-muted font-medium border border-black/[0.07] max-w-xs mx-auto text-center">
              app.elevatexgroup.com
            </div>
          </div>

          {/* Real dashboard screenshot */}
          <div className="relative bg-gray-950 overflow-hidden">
            {/* Premium glow effects */}
            <div className="absolute inset-0 pointer-events-none z-10">
              <div className="absolute top-0 left-1/4 w-96 h-32 bg-primary/20 blur-3xl rounded-full" />
              <div className="absolute bottom-0 right-1/4 w-96 h-32 bg-primary/10 blur-3xl rounded-full" />
            </div>
            <img
              src="/assets/images/image-1774538739507.png"
              alt="ElevateX coach dashboard showing client overview, RAG status tracking, check-in reports and AI assistant activity"
              className="w-full h-auto block relative z-0"
              style={{ display: 'block' }}
            />
          </div>
        </div>

        {/* Caption */}
        <div className="text-center">
          <p className="text-base text-muted font-medium">
            Your AI Assistants work in the background. You see the results here.
          </p>
        </div>

        {/* Platform proof points */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            {
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ),
              title: 'Real Login. Real Access.',
              description: 'Sign in at app.elevatexgroup.com — your own coach account, your own clients, your own data.',
            },
            {
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
              title: 'Live RAG Tracking.',
              description: 'Every client\'s status updated weekly. Green, Amber, Red — visible at a glance, no manual work.',
            },
            {
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              ),
              title: 'Live Software. Not a Service.',
              description: 'ElevateX is a working product — not a consultancy, not a template. It runs your backend automatically.',
            },
          ]?.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-3xl p-7 border border-black/[0.07] hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
            >
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-5">
                {item?.icon}
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">{item?.title}</h3>
              <p className="text-muted text-sm leading-relaxed">{item?.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
